import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
   FormBuilder,
   FormGroup,
   ReactiveFormsModule,
   Validators
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageService } from 'primeng/api';
import { BillingSetupRequest } from '../../services/request/billing-setup-request';
import { BillingService } from '../../services/billing.service';
import { PropertyBillingSummary } from '../../services/response/property-billing-summary';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { PricingService } from '../../services/pricing.service';
import { PricingTier } from '../../services/response/pricing-tier';
import { Subject, of, takeUntil, switchMap, catchError } from 'rxjs';
import { Property } from '../../domain/property';

@Component({
   selector: 'app-billing-setup',
   standalone: true,
   imports: [
      CommonModule,
      ReactiveFormsModule,
      InputTextModule,
      DropdownModule,
      CheckboxModule,
      PanelModule,
      ButtonModule,
      ToastModule,
      RadioButtonModule
   ],
   templateUrl: './billing-setup.component.html',
   styleUrls: ['./billing-setup.component.scss'],
   providers: [MessageService]
})
export class BillingSetupComponent implements OnInit, OnDestroy {
   @Output() submitBilling = new EventEmitter<BillingSetupRequest>();

   billingForm: FormGroup;
   saving = false;
   loading = true;
   currentProperty: Property | null = null;
   propertyId!: number;

   pricingTiers: PricingTier[] = [];
   filteredPricingTiers: PricingTier[] = [];
   selectedTier: PricingTier | null = null;
   discountRate = 0.15;
   billingCycle = 1;
   setupSuccess = false;

   hiddenTierCodes = ['TYL-005'];
   private destroy$ = new Subject<void>();
   existingBillingSummary: PropertyBillingSummary | null = null;

   constructor(
      private fb: FormBuilder,
      private messageService: MessageService,
      private billingService: BillingService,
      private propStore: PropertyStoreService,
      private pricingService: PricingService
   ) {
      this.billingForm = this.fb.group({
         // Business Info
         legalName: [''],
         tradingName: ['', Validators.required],

         // Primary Contact
         primaryContactFirstName: ['', Validators.required],
         primaryContactLastName: ['', Validators.required],
         primaryContactEmail: ['', [Validators.required, Validators.email]],

         // Secondary Contact
         addSecondaryContact: [false],
         secondaryContactFirstName: [''],
         secondaryContactLastName: [''],
         secondaryContactEmail: ['', Validators.email],

         // Billing Address
         billingAttention: [''],
         billingAddressLine1: ['', Validators.required],
         billingAddressCity: ['', Validators.required],
         billingAddressState: [''],
         billingAddressZipCode: ['', Validators.required],
         billingAddressCountry: ['', Validators.required],

         // Tax Info
         taxNumber: [''],

         // Plan Info
         pricingTierCode: ['', Validators.required],
         billingCycle: [
            1,
            [Validators.required, Validators.min(1), Validators.max(12)]
         ]
      });
   }

   ngOnInit() {
      this.loadPricingTiers();

      this.propStore.currentProperty
         .pipe(
            takeUntil(this.destroy$),
            switchMap((p) => {
               this.currentProperty = p ?? null;
               if (!p?.id) return of(null);
               this.propertyId = p.id;
               return this.billingService.getPropertyBillingSummary(p.id).pipe(
                  catchError((err) => {
                     if (err.status === 404) {
                        this.existingBillingSummary = null;
                        return of(null);
                     }
                     this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Unable to fetch billing summary.'
                     });
                     return of(null);
                  })
               );
            })
         )
         .subscribe((summary) => {
            this.existingBillingSummary = summary;
            this.loading = false;
         });
   }

   private loadPricingTiers(): void {
      this.pricingService.getPricingTiers().subscribe({
         next: (tiers) => {
            this.pricingTiers = tiers;
            this.filteredPricingTiers = tiers.filter(
               (t) => t.isActive && !this.hiddenTierCodes.includes(t.pricingTierCode)
            );
         },
         error: () => {
            this.messageService.add({
               severity: 'error',
               summary: 'Error',
               detail: 'Failed to load pricing tiers.'
            });
         }
      });
   }

   getDisplayPrice(tier: PricingTier): number {
      const basePrice = tier.displayPriceMonthly || 0;
      if (this.billingForm.get('billingCycle')?.value === 12) {
         return +(basePrice * (1 - this.discountRate)).toFixed(2);
      }
      return basePrice;
   }

   isInvalid(controlName: string): boolean {
      const control = this.billingForm.get(controlName);
      return !!(control && control.invalid && (control.touched || control.dirty));
   }

   onSubmit() {
      if (this.billingForm.invalid) {
         this.billingForm.markAllAsTouched();
         this.messageService.add({
            severity: 'warn',
            summary: 'Missing Fields',
            detail: 'Please complete all required fields.'
         });
         return;
      }

      if (!this.propertyId) {
         this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No property selected.'
         });
         return;
      }

      this.saving = true;
      const payload: BillingSetupRequest = this.billingForm.value;

      console.log('Submitting billing payload:', payload);

      const formValue = { ...this.billingForm.value };

      if (!formValue.addSecondaryContact) {
         delete formValue.secondaryContactFirstName;
         delete formValue.secondaryContactLastName;
         delete formValue.secondaryContactEmail;
      }

      this.billingService.createXeroContact(this.propertyId, formValue).subscribe({
         next: () => {
            this.messageService.add({
               severity: 'success',
               summary: 'Success',
               detail: 'Xero contact created successfully.'
            });
            this.saving = false;
            this.setupSuccess = true;
         },
         error: (err) => {
            console.error('Xero contact creation failed:', err);
            this.messageService.add({
               severity: 'error',
               summary: 'Error',
               detail: 'Failed to create Xero contact.'
            });
            this.saving = false;
         }
      });
   }

   get isBillingSetupComplete(): boolean {
      return (
         !this.loading &&
         !!(this.existingBillingSummary?.billingId || this.setupSuccess)
      );
   }

   get shouldShowForm(): boolean {
      return !this.loading && !this.isBillingSetupComplete;
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
